import styles from '@/components/book/SaveState.module.css';
import { formatClock } from '@/lib/book/format';

export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date; notice?: string }
  | { kind: 'error'; onRetry: () => void; message?: string; onDiscard?: () => void }
  | { kind: 'offline' }
  | { kind: 'conflict'; at: Date; message?: string; onKeepMine: () => void; onTakeTheirs: () => void }
  | { kind: 'notice'; message: string };

/** The edit row's save state, in words only (DESIGN-SPEC §6.3). */
export function SaveState({ status }: { status: SaveStatus }) {
  if (status.kind === 'idle') return null;
  return (
    <span className={styles.state} role="status">
      <SaveWords status={status} />
    </span>
  );
}

function SaveWords({ status }: { status: Exclude<SaveStatus, { kind: 'idle' }> }) {
  switch (status.kind) {
    case 'pending':
      return <span className={styles.pending}>Unsaved changes</span>;
    case 'saving':
      return <span>Saving…</span>;
    case 'saved':
      return (
        <span>
          Saved {formatClock(status.at)}
          {status.notice && <> · {status.notice}</>}
        </span>
      );
    case 'error':
      return (
        <>
          <span className={styles.alarm}>{status.message ?? 'Not saved.'}</span>
          <button type="button" className={styles.action} onClick={status.onRetry}>Retry</button>
          {status.onDiscard ? <button type="button" className={styles.action} onClick={status.onDiscard}>Discard unread draft</button> : null}
        </>
      );
    case 'offline':
      return (
        <>
          <span className={styles.alarm}>Offline.</span>
          <span className={styles.hint}>Saves when you reconnect.</span>
        </>
      );
    case 'conflict':
      return (
        <>
          <span className={styles.alarm}>{status.message ?? `Changed elsewhere at ${formatClock(status.at)}.`}</span>
          <button type="button" className={styles.action} onClick={status.onKeepMine}>Keep mine</button>
          <span aria-hidden="true">·</span>
          <button type="button" className={styles.action} onClick={status.onTakeTheirs}>Take theirs</button>
        </>
      );
    case 'notice':
      return <span className={styles.alarm}>{status.message}</span>;
  }
}
